/**
 * Environment Configuration System
 * Centralized configuration for different environments (dev, staging, prod)
 */

export type Environment = 'development' | 'staging' | 'production';

export interface EnvironmentConfig {
  name: Environment;
  supabase: {
    url: string;
    anonKey: string;
    // serviceRoleKey removed — must NEVER be in frontend code
  };
  api: {
    baseUrl: string;
    timeout: number;
  };
  features: {
    debugMode: boolean;
    enableLogs: boolean;
    enableAnalytics: boolean;
  };
  external: {
    googleMapsApiKey?: string;
  };
}

/**
 * Get environment variables with proper typing
 */
function getEnvVar(key: string): string | undefined {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return (import.meta.env as Record<string, any>)[key];
  }
  return undefined;
}

/**
 * Get current environment from various sources
 */
export function getCurrentEnvironment(): Environment {
  // Debug logging
  console.log('=== ENVIRONMENT DEBUG ===');
  console.log('VITE_APP_ENV:', import.meta.env.VITE_APP_ENV);
  console.log('VITE_SUPABASE_URL:', import.meta.env.VITE_SUPABASE_URL);
  console.log('VITE_SUPABASE_ANON_KEY (first 50 chars):', import.meta.env.VITE_SUPABASE_ANON_KEY?.substring(0, 50));
  
  // Check for explicit environment variable first - this has highest priority
  if (import.meta.env.VITE_APP_ENV === 'staging') {
    console.log('Using staging from VITE_APP_ENV');
    return 'staging';
  }
  
  if (import.meta.env.VITE_APP_ENV === 'production') {
    console.log('Using production from VITE_APP_ENV');
    return 'production';
  }
  
  // Force staging for deploy preview and branch deploy URLs
  const hostname = window.location.hostname;
  console.log('Current hostname:', hostname);
  
  // Check for Netlify deploy previews and branch deploys (format: xxxxx--lokalaproducenter.netlify.app)
  if (hostname.includes('--lokalaproducenter.netlify.app') && 
      !hostname.includes('lokalaproducenter.netlify.app')) {
    console.log('Detected Netlify deploy preview/branch deploy, using staging');
    return 'staging';
  }
  
  // Check for staging subdomain
  if (hostname.includes('staging')) {
    console.log('Detected staging hostname, using staging');
    return 'staging';
  }
  
  // Check for localhost
  if (hostname.includes('localhost')) {
    console.log('Detected localhost, using development');
    return 'development';
  }
  
  // Default to production for lokalaproducenter.netlify.app and lokalaproducenter.se
  console.log('Using production (default)');
  console.log('=== END ENVIRONMENT DEBUG ===');
  return 'production';
}

/**
 * Development environment configuration
 */
const developmentConfig: EnvironmentConfig = {
  name: 'development',
  supabase: {
    url: getEnvVar('VITE_SUPABASE_URL') || 'http://localhost:54321',
    anonKey: getEnvVar('VITE_SUPABASE_ANON_KEY') || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24iLCJleHAiOjE5ODM4MTI5OTZ9.CRXP1A7WOeoJeXxjNni43kdQwgnWNReilDMblYTn_I0',
  },
  api: {
    baseUrl: 'http://localhost:5173',
    timeout: 10000,
  },
  features: {
    debugMode: true,
    enableLogs: true,
    enableAnalytics: false,
  },
  external: {
    googleMapsApiKey: getEnvVar('VITE_GOOGLE_MAPS_API_KEY'),
  },
};

/**
 * Production environment configuration - now uses environment variables from .env
 */
const productionConfig: EnvironmentConfig = {
  name: 'production',
  supabase: {
    url: getEnvVar('VITE_SUPABASE_URL') || 'https://cuprxjsmcwqcixqpwlph.supabase.co',
    anonKey: getEnvVar('VITE_SUPABASE_ANON_KEY') || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImN1cHJ4anNtY3dxY2l4cXB3bHBoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDAwNDc0MTYsImV4cCI6MjA1NTYyMzQxNn0.oujvytJXllo-mI-kLlHkhf73AxYqlZ5s9PyzR1zHBJA',
  },
  api: {
    baseUrl: 'https://lokalaproducenter.se',
    timeout: 30000,
  },
  features: {
    debugMode: false,
    enableLogs: false,
    enableAnalytics: true,
  },
  external: {
    googleMapsApiKey: getEnvVar('VITE_GOOGLE_MAPS_API_KEY'),
  },
};

/**
 * Staging environment configuration - uses environment variables from .env.staging
 */
const stagingConfig: EnvironmentConfig = {
  name: 'staging',
  supabase: {
    url: getEnvVar('VITE_SUPABASE_URL') || 'https://lokalaproducenter-staging-supabase.ngrok.dev',
    anonKey: getEnvVar('VITE_SUPABASE_ANON_KEY') || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImN1cHJ4anNtY3dxY2l4cXB3bHBoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzUzMTY3MzksImV4cCI6MjA1MDg5MjczOX0.QW-pv55i1UJhb3gFvV3LZ3eGMEvF2FGJ60_j8FjGsNs',
  },
  api: {
    baseUrl: 'https://staging.lokalaproducenter.se',
    timeout: 20000,
  },
  features: {
    debugMode: true,
    enableLogs: true,
    enableAnalytics: false,
  },
  external: {
    googleMapsApiKey: getEnvVar('VITE_GOOGLE_MAPS_API_KEY'),
  },
};

/**
 * Get configuration for specific environment
 */
export function getEnvironmentConfig(env?: Environment): EnvironmentConfig {
  const currentEnv = env || getCurrentEnvironment();
  
  switch (currentEnv) {
    case 'development':
      return developmentConfig;
    case 'staging':
      return stagingConfig;
    case 'production':
      return productionConfig;
    default:
      console.warn(`Unknown environment: ${currentEnv}, falling back to development`);
      return developmentConfig;
  }
}

/**
 * Get current environment configuration
 */
export function getConfig(): EnvironmentConfig {
  return getEnvironmentConfig();
}

/**
 * Utility functions for environment checks
 */
export const isProduction = () => getCurrentEnvironment() === 'production';
export const isDevelopment = () => getCurrentEnvironment() === 'development';
export const isStaging = () => getCurrentEnvironment() === 'staging';

/**
 * Environment-aware console logging
 */
export const envConsole = {
  log: (...args: any[]) => {
    const config = getConfig();
    if (config.features.enableLogs) {
      console.log(`[${config.name.toUpperCase()}]`, ...args);
    }
  },
  warn: (...args: any[]) => {
    const config = getConfig();
    if (config.features.enableLogs) {
      console.warn(`[${config.name.toUpperCase()}]`, ...args);
    }
  },
  error: (...args: any[]) => {
    // Always log errors regardless of environment
    const config = getConfig();
    console.error(`[${config.name.toUpperCase()}]`, ...args);
  },
  debug: (...args: any[]) => {
    const config = getConfig();
    if (config.features.debugMode && config.features.enableLogs) {
      console.debug(`[${config.name.toUpperCase()}-DEBUG]`, ...args);
    }
  },
};

// Log current environment on module load
envConsole.log('Environment configuration loaded:', getCurrentEnvironment()); 